/**
 * @fileoverview Hook Actions Layer para CustomerReminderStatusSelectorMC. Maneja operaciones de negocio y side effects.
 * @module useCustomerReminderStatusActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import remindersModelMC from '../../../models/RemindersModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';

/**
 * Hook Actions Layer para CustomerReminderStatusSelectorMC.
 * Separa la lógica de negocio y side effects de la UI.
 *
 * @param {Object} params
 * @param {Object} params.reminder - Objeto reminder actual
 * @param {Function} params.setCustomerReminders - Setter para actualizar reminders
 * @param {Function} params.setAlertTitle - Setter para el título de alerta
 * @param {Object} params.confirmAlertRef - Ref del Alert para control visual
 * @returns {Object} loading, handleConfirm
 */
export default function useCustomerReminderStatusActions({ reminder, setCustomerReminders, setAlertTitle, confirmAlertRef }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Actualiza el estado del recordatorio (cancelar)
  const updateReminderStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await remindersModelMC.cancelReminder(reminder.web_id);
      setCustomerReminders(response?.reminders || []);
      if (confirmAlertRef.current) confirmAlertRef.current.toggleAlert();
    } catch (error) {
      dispatch({ type: LOAD_DATA_FAILURE, error: error.message || 'Error updating customer reminder.' });
      setAlertTitle('');
    } finally {
      setLoading(false);
    }
  }, [dispatch, reminder?.web_id, setCustomerReminders, setAlertTitle, confirmAlertRef]);

  // Handler para confirmar cambio
  const handleConfirm = useCallback(() => {
    updateReminderStatus();
  }, [updateReminderStatus]);

  return {
    loading,
    handleConfirm,
  };
}
